import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSiteMeta } from "@/hooks/useSiteMeta";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useLanguage } from "@/contexts/LanguageContext";
import { SITE_CONFIG } from "@/lib/constants";

interface DynamicHeadProps {
  pageTitle?: string;
  pageDescription?: string;
  canonicalPath?: string;
  ogImage?: string;
  noIndex?: boolean;
  /** Explicit per-language paths (for pages whose slugs differ per language) */
  alternates?: Partial<Record<"nl" | "en" | "fr", string>>;
}

const BASE_URL = SITE_CONFIG.baseUrl;

// Language to locale mapping for hreflang and OG
const LANG_LOCALES: Record<string, { hreflang: string; ogLocale: string }> = {
  nl: { hreflang: "nl-NL", ogLocale: "nl_NL" },
  en: { hreflang: "en-GB", ogLocale: "en_GB" },
  fr: { hreflang: "fr-FR", ogLocale: "fr_FR" },
};

export const DynamicHead = ({ 
  pageTitle, 
  pageDescription, 
  canonicalPath,
  ogImage,
  noIndex = false,
  alternates,
}: DynamicHeadProps) => {
  const { data: siteMeta } = useSiteMeta();
  const { data: settings } = useSiteSettings();
  const { currentLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    if (!siteMeta) return;

    // Update document title
    const title = pageTitle 
      ? `${pageTitle} | ${siteMeta.site_title}`
      : siteMeta.site_title;
    document.title = title;

    // Update meta description
    const description = pageDescription || siteMeta.site_description;
    updateMetaTag("description", description || "");

    // Canonical URL
    const currentPath = canonicalPath || location.pathname;
    const canonicalUrl = `${BASE_URL}${currentPath}`;
    updateLinkTag("canonical", canonicalUrl);

    // OG tags
    updateMetaTag("og:title", pageTitle || siteMeta.og_title || title, "property");
    updateMetaTag("og:description", description || siteMeta.og_description || "", "property");
    updateMetaTag("og:url", canonicalUrl, "property");
    updateMetaTag("og:type", "website", "property");
    updateMetaTag("og:site_name", siteMeta.site_title, "property");
    
    // OG Image
    const finalOgImage = ogImage || siteMeta.og_image || `${BASE_URL}/og-image.png`;
    updateMetaTag("og:image", finalOgImage, "property");
    updateMetaTag("og:image:width", "1200", "property");
    updateMetaTag("og:image:height", "630", "property");

    // OG Locale
    const localeInfo = LANG_LOCALES[currentLanguage] || LANG_LOCALES.nl;
    updateMetaTag("og:locale", localeInfo.ogLocale, "property");
    
    // Add alternate locales
    Object.entries(LANG_LOCALES).forEach(([lang, info]) => {
      if (lang !== currentLanguage) {
        updateMetaTag(`og:locale:alternate`, info.ogLocale, "property", `og-locale-${lang}`);
      }
    });

    // Twitter tags
    updateMetaTag("twitter:card", siteMeta.twitter_card || "summary_large_image");
    updateMetaTag("twitter:title", pageTitle || title);
    updateMetaTag("twitter:description", description || "");
    updateMetaTag("twitter:image", finalOgImage);
    if (siteMeta.twitter_site) {
      updateMetaTag("twitter:site", siteMeta.twitter_site);
    }

    // Hreflang tags for multilingual SEO
    updateHreflangTags(currentPath, currentLanguage, alternates);

    // Robots meta tag
    if (noIndex) {
      updateMetaTag("robots", "noindex, nofollow");
    } else {
      updateMetaTag("robots", "index, follow");
    }

    // Update favicon
    if (settings?.favicon_url) {
      updateFavicon(settings.favicon_url);
    }
  }, [siteMeta, settings, pageTitle, pageDescription, canonicalPath, ogImage, noIndex, currentLanguage, location.pathname, alternates?.nl, alternates?.en, alternates?.fr]);

  return null;
};

function updateMetaTag(
  name: string, 
  content: string, 
  attribute: "name" | "property" = "name",
  customId?: string
) {
  const id = customId || `meta-${attribute}-${name.replace(/:/g, "-")}`;
  let meta = document.getElementById(id) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement("meta");
    meta.id = id;
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

function updateLinkTag(rel: string, href: string, hreflang?: string) {
  const id = hreflang ? `link-${rel}-${hreflang}` : `link-${rel}`;
  let link = document.getElementById(id) as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = rel;
    if (hreflang) {
      link.setAttribute("hreflang", hreflang);
    }
    document.head.appendChild(link);
  }
  
  link.href = href;
}

function updateHreflangTags(currentPath: string, currentLanguage: string, alternates?: Partial<Record<string, string>>) {
  // Remove existing hreflang links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

  if (alternates) {
    Object.entries(LANG_LOCALES).forEach(([lang, info]) => {
      const p = alternates[lang];
      if (p) updateLinkTag("alternate", `${BASE_URL}${p}`, info.hreflang);
    });
    if (alternates.nl) updateLinkTag("alternate", `${BASE_URL}${alternates.nl}`, "x-default");
    return;
  }
  
  // Generate alternate URLs for each language
  Object.entries(LANG_LOCALES).forEach(([lang, info]) => {
    let alternatePath: string;
    
    if (lang === "nl") {
      // Dutch is the default, no prefix needed
      alternatePath = currentPath.replace(/^\/(en|fr)/, "") || "/";
    } else {
      // Remove any existing language prefix and add the new one
      const pathWithoutLang = currentPath.replace(/^\/(en|fr)/, "") || "/";
      alternatePath = `/${lang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    }
    
    const alternateUrl = `${BASE_URL}${alternatePath}`;
    updateLinkTag("alternate", alternateUrl, info.hreflang);
  });
  
  // Add x-default (points to Dutch version)
  const defaultPath = currentPath.replace(/^\/(en|fr)/, "") || "/";
  updateLinkTag("alternate", `${BASE_URL}${defaultPath}`, "x-default");
}

function updateFavicon(url: string) {
  let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  
  link.href = url;
}

export default DynamicHead;
